function _extends() {
  return _extends = Object.assign ? Object.assign.bind() : function (n) {
    for (var e = 1; e < arguments.length; e++) {
      var t = arguments[e];
      for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]);
    }
    return n;
  }, _extends.apply(null, arguments);
}
import './InteractiveGoogleMap.css';
import React, { useEffect, useState, useRef } from 'react';
import useSupercluster from 'use-supercluster';
import GoogleMapReact from 'google-map-react';
import MarkerInfoItem from './components/MarkerInfoItem';
import { SitecoreContextReactContext } from '../../../utils/SitecoreContextReactContext';
import { canUseDOM, get } from '../../../utils';
import { stylesOldCarousel } from '../../../utils/OldCarouselDotsStyles';
import { tealiumClickFunc, tealiumMergeClickData } from '../../../utils/tealium';
const Marker = _ref => {
  let {
    children
  } = _ref;
  return children;
};
const convertDataToGEOJson = markers => {
  return markers.map(el => {
    const {
      _geoloc: {
        lng
      },
      _geoloc: {
        lat
      },
      objectID
    } = el;
    if (objectID && lng && lat) {
      return {
        type: 'Feature',
        properties: {
          category: 'wells',
          cluster: false,
          wellId: `${objectID}`
        },
        geometry: {
          type: 'Point',
          coordinates: [lng, lat]
        },
        data: el
      };
    }
  });
};
const mapStyles = [{
  featureType: 'water',
  elementType: 'geometry',
  stylers: [{
    color: '#C7C7C7'
  }, {
    lightness: 17
  }]
}, {
  featureType: 'landscape',
  elementType: 'geometry',
  stylers: [{
    color: '#f5f5f5'
  }, {
    lightness: 20
  }]
}, {
  featureType: 'road.highway',
  elementType: 'geometry.fill',
  stylers: [{
    color: '#ffffff'
  }, {
    lightness: 17
  }]
}, {
  featureType: 'road.highway',
  elementType: 'geometry.stroke',
  stylers: [{
    color: '#ffffff'
  }, {
    lightness: 29
  }, {
    weight: 0.2
  }]
}, {
  featureType: 'road.arterial',
  elementType: 'geometry',
  stylers: [{
    color: '#ffffff'
  }, {
    lightness: 18
  }]
}, {
  featureType: 'road.local',
  elementType: 'geometry',
  stylers: [{
    color: '#ffffff'
  }, {
    lightness: 16
  }]
}, {
  featureType: 'poi',
  elementType: 'geometry',
  stylers: [{
    color: '#f5f5f5'
  }, {
    lightness: 21
  }]
}, {
  featureType: 'poi.park',
  elementType: 'geometry',
  stylers: [{
    color: '#dedede'
  }, {
    lightness: 21
  }]
}, {
  elementType: 'labels.text.stroke',
  stylers: [{
    visibility: 'on'
  }, {
    color: '#ffffff'
  }, {
    lightness: 16
  }]
}, {
  elementType: 'labels.text.fill',
  stylers: [{
    saturation: 36
  }, {
    color: '#333333'
  }, {
    lightness: 40
  }]
}, {
  elementType: 'labels.icon',
  stylers: [{
    visibility: 'off'
  }]
}, {
  featureType: 'transit',
  elementType: 'geometry',
  stylers: [{
    color: '#f2f2f2'
  }, {
    lightness: 19
  }]
}, {
  featureType: 'administrative',
  elementType: 'geometry.fill',
  stylers: [{
    color: '#fefefe'
  }, {
    lightness: 20
  }]
}, {
  featureType: 'administrative',
  elementType: 'geometry.stroke',
  stylers: [{
    color: '#fefefe'
  }, {
    lightness: 17
  }, {
    weight: 1.2
  }]
}];
const InteractiveGoogleMap = _ref2 => {
  let {
    mapState,
    callback,
    handleGetHitsFromMap,
    handleGetActiveItemId,
    dictionary,
    centreMapLongitude,
    centreMapLatitude,
    defaultMapZoom,
    analytics,
    params,
    rtl,
    activePopupItem,
    activeTopPicks,
    toggleActiveTopPicks
  } = _ref2;
  const sitecoreContext = React.useContext(SitecoreContextReactContext);
  const googleApiKey = get(sitecoreContext, 'configuration.google.apiKey', '');
  const [bounds, setBounds] = useState(undefined);
  const [zoom, setZoom] = useState(defaultMapZoom);
  const [activeMarker, setActiveMarker] = useState(null);
  const [markers, setMarkers] = useState([]);
  const [shouldListBeRerendered, setShouldListBeRerendered] = useState(false);
  const [dafaultCenter, setDefaultCenter] = useState({
    lat: Number(centreMapLatitude),
    lng: Number(centreMapLongitude)
  });
  const mapRef = useRef();
  const maxZoom = 16;
  const maxMapZoom = 20;
  const arrangePinsInCircle = (centerX, centerY, radius, numberOfPins) => {
    const positions = [];
    const angleStep = 2 * Math.PI / numberOfPins;
    for (let i = 0; i < numberOfPins; i++) {
      const angle = i * angleStep;
      const x = centerX + radius * Math.cos(angle);
      const y = centerY + radius * Math.sin(angle);
      positions.push({
        lat: y,
        lng: x
      });
    }
    return positions;
  };
  const filterPins = markers => {
    let finalArr = [];
    const getAllMarkersArr = markers.map(el => {
      return {
        id: el.properties.wellId,
        lat: el.data._geoloc.lat,
        lng: el.data._geoloc.lng
      };
    });
    let getAllLatitudeDublicatesArr = getAllMarkersArr.reduce((acc, item) => {
      const lat = item.lat;
      (acc[lat] = acc[lat] || []).push(item);
      return acc;
    }, {});
    for (const property in getAllLatitudeDublicatesArr) {
      const dublicatedByLatitudeArray = getAllLatitudeDublicatesArr[property];
      if (dublicatedByLatitudeArray.length > 1) {
        // X-coordinate of the center point
        const centerX = dublicatedByLatitudeArray[0].lng;
        // Y-coordinate of the center point
        const centerY = dublicatedByLatitudeArray[0].lat;
        // Radius of the circle
        const radius = 0.00005;
        // Number of pins
        const numberOfPins = dublicatedByLatitudeArray.length;
        const pinPositions = arrangePinsInCircle(centerX, centerY, radius, numberOfPins);
        dublicatedByLatitudeArray.map((el, i) => {
          el.lat = pinPositions[i].lat;
          el.lng = pinPositions[i].lng;
          finalArr.push(el);
        });
      }
    }
    finalArr = markers.map(el => {
      const updatedItemWithNewCoordinates = finalArr.filter(item => el.properties.wellId === item.id);
      if (updatedItemWithNewCoordinates.length) {
        const {
          lat,
          lng
        } = updatedItemWithNewCoordinates[0];
        el.data._geoloc.lat = Number(lat);
        el.data._geoloc.lng = Number(lng);
        el.geometry.coordinates[0] = Number(lng);
        el.geometry.coordinates[1] = Number(lat);
      }
      return el;
    });
    return finalArr;
  };
  useEffect(() => {
    if (mapState && mapState.markers) {
      clearActiveMarkerId();
      let convertedMarkers = convertDataToGEOJson(mapState.markers);
      convertedMarkers = filterPins(convertedMarkers);
      if (mapRef.current && convertedMarkers && convertedMarkers.length) {
        const lat = convertedMarkers[0] === undefined ? null : convertedMarkers[0].geometry.coordinates[1];
        const lng = convertedMarkers[0] === undefined ? null : convertedMarkers[0].geometry.coordinates[0];
        if (lat && lng) {
          setZoom(defaultMapZoom);
          mapRef.current.setZoom(defaultMapZoom);
          mapRef.current.panTo({
            lat,
            lng
          });
        }
      }
      setMarkers(convertedMarkers);
      centerMap();
    }
  }, [mapState]);
  const {
    clusters,
    supercluster
  } = useSupercluster({
    points: markers,
    bounds,
    zoom,
    options: {
      radius: 85,
      maxZoom: maxZoom
    }
  });
  React.useEffect(() => {
    if (shouldListBeRerendered) {
      updateMarkersToTheList();
    }
  }, [clusters, shouldListBeRerendered]);
  React.useEffect(() => {
    if (activePopupItem) {
      const {
        objectID,
        lat,
        lng
      } = activePopupItem;
      setActiveMarker(objectID);
      handleGetActiveItemId(objectID);
      mapRef.current.setZoom(maxZoom + 1);
      mapRef.current.panTo({
        lat: lat,
        lng: lng
      });
    }
  }, [activePopupItem]);
  const _handleChangeValue = _ref3 => {
    let {
      zoom,
      bounds
    } = _ref3;
    setZoom(zoom);
    setBounds([bounds.nw.lng, bounds.se.lat, bounds.se.lng, bounds.nw.lat]);
  };
  const handleActiveMarker = (objectID, latitude, longitude) => {
    mapRef.current.panTo({
      lat: latitude,
      lng: longitude
    });
    setActiveMarker(objectID);
    handleGetActiveItemId(objectID);
  };
  const clearActiveMarkerId = () => {
    setActiveMarker(null);
    handleGetActiveItemId(null);
  };
  const updateMarkersToTheList = () => {
    handleGetHitsFromMap(clusters);
  };
  const centerMap = () => {
    if (mapRef.current) {
      mapRef.current.setZoom(10);
      mapRef.current.panTo(dafaultCenter);
    }
  };
  const getClusterIconSize = count => {
    if (count >= 100) {
      return 68;
    }
    return 42;
  };
  return /*#__PURE__*/React.createElement(StyledWrapper, {
    rtl: rtl
  }, /*#__PURE__*/React.createElement("div", {
    className: "im-google-map"
  }, /*#__PURE__*/React.createElement("div", {
    className: "im-google-map-wrapper"
  }, /*#__PURE__*/React.createElement(GoogleMapReact, {
    bootstrapURLKeys: {
      key: !process.env.STORYBOOK_MODE && canUseDOM ? googleApiKey : 'AIzaSyB4GL2flAQBGxzOjrbVXQUvpU2UqJfoPo8'
    },
    defaultCenter: dafaultCenter,
    defaultZoom: 10,
    yesIWantToUseGoogleMapApiInternals: true,
    onGoogleApiLoaded: _ref4 => {
      let {
        map
      } = _ref4;
      callback();
      mapRef.current = map;
    },
    options: {
      fullscreenControl: false,
      zoomControl: false,
      clickableIcons: false,
      styles: mapStyles
    },
    onChange: _handleChangeValue,
    onZoomAnimationStart: () => {
      if (!shouldListBeRerendered) {
        setShouldListBeRerendered(true);
      }
    },
    onDragEnd: () => {
      clearActiveMarkerId();
      if (!shouldListBeRerendered) {
        setShouldListBeRerendered(true);
      }
    },
    onClick: e => {
      if (e.event.target.nodeName !== 'svg' && e.event.target.nodeName !== 'path' && !e.event.target.className.includes('carousel-icon') && !e.event.target.className.includes('imfilters-listing-item__fav-btn') && !e.event.target.className.includes('dot')) {
        clearActiveMarkerId();
      }
    }
  }, clusters && clusters.map(cluster => {
    const [longitude, latitude] = cluster.geometry.coordinates;
    const {
      cluster: isCluster,
      point_count: pointCount
    } = cluster.properties;
    if (isCluster) {
      return /*#__PURE__*/React.createElement(Marker, {
        lat: latitude,
        lng: longitude,
        key: `cluster-${cluster.id}`,
        className: "cluster-marker"
      }, /*#__PURE__*/React.createElement("button", {
        type: "button",
        className: "cluster-marker",
        style: {
          width: `${getClusterIconSize(pointCount)}px`,
          height: `${getClusterIconSize(pointCount)}px`
        },
        onClick: () => {
          if (supercluster && mapRef.current && cluster.id) {
            const expansionZoom = Math.min(supercluster.getClusterExpansionZoom(Number(cluster.id)), maxMapZoom);
            mapRef.current.setZoom(expansionZoom);
            mapRef.current.panTo({
              lat: latitude,
              lng: longitude
            });
          }
        }
      }, pointCount));
    } else {
      return /*#__PURE__*/React.createElement(Marker, {
        key: `cluster-${cluster.properties.wellId}`,
        lat: latitude,
        lng: longitude
      }, /*#__PURE__*/React.createElement("button", {
        type: "button",
        className: `well-marker ${activeMarker === cluster.data.objectID ? 'active' : ''} `,
        onClick: () => {
          handleActiveMarker(cluster.data.objectID, latitude, longitude);
          if (!shouldListBeRerendered) {
            setShouldListBeRerendered(true);
          }
          tealiumClickFunc(tealiumMergeClickData(analytics['mapPinAnalytics'], params), `${cluster.data.title ? cluster.data.title : ''}`);
        }
      }, /*#__PURE__*/React.createElement("svg", {
        xmlns: "http://www.w3.org/2000/svg",
        width: "34.661",
        height: "43.564",
        viewBox: "0 0 34.661 43.564"
      }, /*#__PURE__*/React.createElement("g", {
        id: "Group_2156",
        "data-name": "Group 2156",
        transform: "translate(0.25 0.248)"
      }, /*#__PURE__*/React.createElement("circle", {
        id: "Ellipse_812",
        "data-name": "Ellipse 812",
        cx: "8",
        cy: "8",
        r: "8",
        transform: "translate(8.242 8.001)",
        fill: "#fff"
      }), /*#__PURE__*/React.createElement("path", {
        id: "Subtraction_303",
        "data-name": "Subtraction 303",
        d: "M-6444.792-1902.43a1.6,1.6,0,0,1-1.138-.471,97.37,97.37,0,0,1-10.713-12.333c-3.481-5.037-5.233-9.624-5.356-14.021v-.084a16.619,16.619,0,0,1,4.978-11.292,17.139,17.139,0,0,1,11.757-4.861c.118,0,.236,0,.354,0a17.5,17.5,0,0,1,11.894,4.567,15.742,15.742,0,0,1,5.176,11.2v.01c.069,4.334-1.292,8.617-4.163,13.093a79.472,79.472,0,0,1-9.049,10.926l-.044.047c-.872.934-1.695,1.816-2.505,2.709a1.592,1.592,0,0,1-1.153.512Zm.292-31.561a4.5,4.5,0,0,0-4.5,4.5,4.505,4.505,0,0,0,4.5,4.5,4.506,4.506,0,0,0,4.5-4.5A4.5,4.5,0,0,0-6444.5-1933.991Z",
        transform: "translate(6462 1945.497)",
        fill: activeMarker === cluster.data.objectID ? '#2B2B2B' : cluster.data.color,
        stroke: "#646464",
        "stroke-width": "0.5"
      })))), activeMarker === cluster.data.objectID ? /*#__PURE__*/React.createElement(MarkerInfoItem, _extends({}, cluster.data, {
        dictionary: dictionary,
        analytics: analytics,
        params: params,
        rtl: rtl,
        activeTopPicks: activeTopPicks,
        toggleActiveTopPicks: toggleActiveTopPicks
      })) : null);
    }
  })), /*#__PURE__*/React.createElement("button", {
    className: "default-center-btn icon-btn",
    name: "name",
    onClick: () => {
      centerMap();
      clearActiveMarkerId();
      tealiumClickFunc(tealiumMergeClickData(analytics['mapInteractionsAnalytics'], params), 'home');
    }
  }, /*#__PURE__*/React.createElement("svg", {
    xmlns: "http://www.w3.org/2000/svg",
    width: "16.282",
    height: "16.169",
    viewBox: "0 0 16.282 16.169"
  }, /*#__PURE__*/React.createElement("g", {
    id: "Group_15486",
    "data-name": "Group 15486",
    transform: "translate(-1883.657 -944.472)"
  }, /*#__PURE__*/React.createElement("path", {
    id: "Path_700",
    "data-name": "Path 700",
    d: "M1890.822,901.913l-6.446,5.125.455,8.291,2.661.56,1.681-.243.163-5.84,2.729-.726,1.442.339,1.288,1.355-.177,4.352,3.574.52,1.072-.916v-6.774l-.5-1.744L1892.4,901.4Z",
    transform: "translate(0 44)",
    fill: "#035e88"
  }), /*#__PURE__*/React.createElement("path", {
    id: "Path_386",
    "data-name": "Path 386",
    d: "M16.992,7.094,10.776,2.359a1.681,1.681,0,0,0-2.031,0L2.531,7.093A1.684,1.684,0,0,0,1.87,8.426V15.96a1.677,1.677,0,0,0,1.675,1.675H7.254a.559.559,0,0,0,.558-.558V11.458a.559.559,0,0,1,.558-.558h2.781a.559.559,0,0,1,.558.558v5.618a.559.559,0,0,0,.558.558h3.708a1.677,1.677,0,0,0,1.675-1.675V8.426A1.684,1.684,0,0,0,16.992,7.094Zm-.457,8.866a.559.559,0,0,1-.558.558h-3.15v-5.06a1.677,1.677,0,0,0-1.675-1.675H8.371A1.677,1.677,0,0,0,6.7,11.458v5.06H3.545a.559.559,0,0,1-.558-.558V8.426a.561.561,0,0,1,.22-.444L9.423,3.248a.56.56,0,0,1,.677,0l6.216,4.735a.561.561,0,0,1,.22.444V15.96Z",
    transform: "translate(1882.037 942.755)",
    fill: "#035e88",
    stroke: "#035e88",
    "stroke-width": "0.5"
  })))), /*#__PURE__*/React.createElement("div", {
    className: "zoom-btns"
  }, /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "icon-btn zoom-plus",
    name: "zoom in",
    disabled: mapRef.current ? mapRef.current.getZoom() === maxMapZoom + 1 : false,
    onClick: () => {
      if (mapRef.current) {
        const currentZoom = mapRef.current.getZoom();
        if (currentZoom != maxMapZoom + 1) {
          mapRef.current.setZoom(currentZoom + 1);
          tealiumClickFunc(tealiumMergeClickData(analytics['mapInteractionsAnalytics'], params), 'zoom in');
        }
      }
    }
  }, /*#__PURE__*/React.createElement("svg", {
    xmlns: "http://www.w3.org/2000/svg",
    width: "10.875",
    height: "10.875",
    viewBox: "0 0 10.875 10.875"
  }, /*#__PURE__*/React.createElement("g", {
    id: "Group_1244",
    "data-name": "Group 1244",
    transform: "translate(-1885.5 -940.5)"
  }, /*#__PURE__*/React.createElement("line", {
    id: "Line_241",
    "data-name": "Line 241",
    y2: "8.875",
    transform: "translate(1890.937 941.5)",
    fill: "none",
    stroke: "#035e88",
    "stroke-linecap": "round",
    "stroke-width": "2"
  }), /*#__PURE__*/React.createElement("line", {
    id: "Line_242",
    "data-name": "Line 242",
    y2: "8.875",
    transform: "translate(1895.375 945.937) rotate(90)",
    fill: "none",
    stroke: "#035e88",
    "stroke-linecap": "round",
    "stroke-width": "2"
  })))), /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "icon-btn zoom-minus",
    name: "zoom out",
    disabled: mapRef.current ? mapRef.current.getZoom() === 3 : false,
    onClick: () => {
      if (mapRef.current) {
        const currentZoom = mapRef.current.getZoom();
        if (currentZoom != 3) {
          mapRef.current.setZoom(currentZoom - 1);
          tealiumClickFunc(tealiumMergeClickData(analytics['mapInteractionsAnalytics'], params), 'zoom out');
        }
      }
    }
  }, /*#__PURE__*/React.createElement("svg", {
    xmlns: "http://www.w3.org/2000/svg",
    width: "10.875",
    height: "2",
    viewBox: "0 0 10.875 2"
  }, /*#__PURE__*/React.createElement("g", {
    id: "Group_1245",
    "data-name": "Group 1245",
    transform: "translate(-1885.5 -944.937)"
  }, /*#__PURE__*/React.createElement("line", {
    id: "Line_242",
    "data-name": "Line 242",
    y2: "8.875",
    transform: "translate(1895.375 945.937) rotate(90)",
    fill: "none",
    stroke: "#035e88",
    "stroke-linecap": "round",
    "stroke-width": "2"
  }))))))));
};
const StyledWrapper = _ref5 => {
  let {
    children,
    rtl
  } = _ref5;
  return /*#__PURE__*/React.createElement("div", {
    className: ` ${commonStyles} ${stylesOldCarousel} ${rtl ? rtlStyles : ltrStyles}`
  }, children);
};
const commonStyles = "c1o5xrtf";
const ltrStyles = "l1oxac3";
const rtlStyles = "r1818wo0";
export default InteractiveGoogleMap;